import type { ReactNode } from "react";
import { Platform, ScrollView, StyleSheet, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { LinearGradient } from "expo-linear-gradient";
import { BlurView } from "expo-blur";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import LivingCellularBackground from "./LivingCellularBackground";
import { LouiseMark } from "./OnboardingLayout";

/**
 * The dark glass of sign-up and log in: the card's material, the orb that
 * crowns it, and the living background behind both. Onboarding draws them
 * around its stack; screens outside that stack that are still part of signing
 * in (the two-factor challenge) use AuthGlassScene so they look the same.
 */
export const AUTH_GLASS = StyleSheet.create({
  glassCard: {
    borderTopLeftRadius: 260,
    borderTopRightRadius: 260,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
    overflow: "hidden",
    backgroundColor: "rgba(16, 11, 8, 0.30)",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    shadowColor: "#130702",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.28,
    shadowRadius: 34,
  },
  glassCardWeb: {
    backdropFilter: "saturate(1.35) blur(14px)",
    WebkitBackdropFilter: "saturate(1.35) blur(14px)",
  },
  glassCardIOS: {
    backgroundColor: "rgba(16, 11, 8, 0.22)",
    borderColor: "transparent",
  },
  glassWarmthIOS: {
    backgroundColor: "rgba(112, 44, 14, 0.12)",
  },
  glassEffect: {
    borderTopLeftRadius: 72,
    borderTopRightRadius: 72,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  },
  glassEffectDesktop: {
    borderRadius: 28,
  },
  glassEffectDocked: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  glassBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  glassWash: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(42, 32, 26, 0.12)",
  },
  orb: {
    alignItems: "center",
    position: "relative",
    zIndex: 30,
  },
  orbLogoLayer: {
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 0,
  },
  orbVisualLayer: {
    position: "relative",
    zIndex: 1,
  },
});

/** The glass layers inside the card: a dark blur on iOS, a backdrop elsewhere. */
export function AuthGlassLayers({
  desktop = false,
  docked = false,
  backdropKey,
}: {
  desktop?: boolean;
  docked?: boolean;
  /** Remounts the web backdrop once the background has painted. */
  backdropKey?: string;
}) {
  if (Platform.OS === "ios") {
    const shape = [
      StyleSheet.absoluteFill,
      AUTH_GLASS.glassEffect,
      desktop && AUTH_GLASS.glassEffectDesktop,
      docked && AUTH_GLASS.glassEffectDocked,
    ];
    return (
      <>
        <BlurView
          pointerEvents="none"
          intensity={32}
          tint="dark"
          experimentalBlurMethod="dimezisBlurView"
          style={shape}
        />
        <View
          pointerEvents="none"
          style={[...shape, AUTH_GLASS.glassWarmthIOS]}
        />
      </>
    );
  }
  return (
    <>
      <View
        key={backdropKey}
        pointerEvents="none"
        style={[
          AUTH_GLASS.glassBackdrop,
          Platform.OS === "web" && AUTH_GLASS.glassCardWeb,
        ]}
      />
      <View pointerEvents="none" style={AUTH_GLASS.glassWash} />
    </>
  );
}

/** The gradient orb with the Louise mark behind it, half over the card. */
export function AuthOrb({ size, testID }: { size: number; testID?: string }) {
  return (
    <View
      testID={testID}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={[AUTH_GLASS.orb, { marginBottom: -size / 2 }]}
    >
      <View style={AUTH_GLASS.orbLogoLayer} pointerEvents="none">
        <LouiseMark size={size * 0.4} />
      </View>
      <View style={AUTH_GLASS.orbVisualLayer}>
        <GradientOrb size={size} />
      </View>
    </View>
  );
}

/**
 * A whole signing-in screen outside the onboarding stack: the living
 * background, the orb and one glass card holding `children`.
 */
export default function AuthGlassScene({
  children,
  testID,
}: {
  children: ReactNode;
  testID?: string;
}) {
  const reducedMotion = useReducedMotion();
  const isIOS = Platform.OS === "ios";
  return (
    <View style={styles.root} testID={testID}>
      <LivingCellularBackground
        motionEnabled={!reducedMotion}
        origin={{ x: 0.5, y: 0.82 }}
      />
      <LinearGradient
        pointerEvents="none"
        colors={[
          "rgba(21, 8, 2, 0.08)",
          "rgba(21, 8, 2, 0.00)",
          "rgba(14, 6, 2, 0.46)",
        ]}
        locations={[0, 0.46, 1]}
        style={StyleSheet.absoluteFill}
      />
      <SafeAreaView style={{ flex: 1 }}>
        <ScrollView
          contentContainerStyle={styles.wrapper}
          bounces={false}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <AuthOrb size={84} />
          <View
            style={[
              AUTH_GLASS.glassCard,
              isIOS && AUTH_GLASS.glassCardIOS,
              styles.card,
            ]}
          >
            <AuthGlassLayers />
            <View style={styles.content}>{children}</View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: "#140A05",
  },
  wrapper: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  card: {
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 72,
    borderTopRightRadius: 72,
  },
  content: {
    alignItems: "center",
    paddingHorizontal: 28,
    paddingTop: 60,
    paddingBottom: 32,
  },
});
